import { Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  COMPLIANCE_FIELD_TYPE_LABELS,
  ComplianceFieldTypeSchema,
  crewRoleLabel,
  countryLabel,
  type ComplianceFieldType,
  type Jurisdiction,
  type RequirementInput,
} from "@gb/contracts";
import { BRAND_LABEL, SectionLabel } from "@/components/gb/section-label";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useJurisdiction,
  useReplaceRequirements,
  useRequirements,
} from "../phases-api";
import { useSetSetupStep } from "../api";
import { Field } from "@/components/gb/field";
import { STEP, StepPanel, WizardFooter, type StepProps } from "./wizard";

const FIELD_TYPES: { value: ComplianceFieldType; label: string }[] =
  ComplianceFieldTypeSchema.options.map((value) => ({
    value,
    label: COMPLIANCE_FIELD_TYPE_LABELS[value],
  }));

type InfoDraft = {
  id?: string;
  label: string;
  dataType: ComplianceFieldType;
  /** Comma-separated while editing; split on save. */
  options: string;
};

export function StepCompliance({ project, mode, goTo }: StepProps) {
  const jurisdiction = useJurisdiction(project?.country ?? undefined);
  const { data: requirements, isPending } = useRequirements();
  const replace = useReplaceRequirements();
  const setStep = useSetSetupStep();

  const [fields, setFields] = useState<InfoDraft[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current || !requirements) return;
    hydrated.current = true;
    setFields(
      requirements.map((requirement) => ({
        id: requirement.id,
        label: requirement.label,
        dataType: requirement.dataType ?? "text",
        options: (requirement.selectOptions ?? []).join(", "),
      })),
    );
  }, [requirements]);

  /**
   * Write the fields as they stand. False means they did not validate and
   * nothing was sent.
   */
  const persist = async (): Promise<boolean> => {
    const nextErrors: Record<number, string> = {};
    fields.forEach((field, index) => {
      if (!field.label.trim()) nextErrors[index] = "Give the field a label.";
      else if (field.dataType === "select" && !field.options.trim()) {
        nextErrors[index] = "List at least one option.";
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return false;
    }
    setErrors({});

    const payload: RequirementInput[] = fields.map((field) => ({
      label: field.label.trim(),
      dataType: field.dataType,
      ...(field.dataType === "select"
        ? {
            selectOptions: field.options
              .split(",")
              .map((option) => option.trim())
              .filter(Boolean),
          }
        : {}),
    }));

    await replace.mutateAsync(payload);
    return true;
  };

  const submit = async (): Promise<void> => {
    if (!(await persist())) return;
    // Skipped in edit mode: the cursor is monotonic and a live project is
    // already at the last step, so the call would be a no-op round trip.
    if (project && mode === "setup") {
      await setStep.mutateAsync({ id: project.id, step: STEP.review });
    }
    goTo(STEP.review);
  };

  /**
   * Back commits, the same as Continue.
   *
   * Leaving a step used to write nothing whichever way you left it, so an edit
   * made and then backed out of was gone. Continue was the only control that
   * saved, which is not something the wizard says anywhere.
   *
   * Invalid input navigates anyway rather than trapping the user: someone
   * pressing Back is often going back *because* something upstream is wrong,
   * and refusing to move until a half-typed row here is fixed would be a worse
   * bug than the one being fixed. What is lost in that case is an unlabelled
   * row — nothing the user would call an edit — and never a valid one.
   */
  const back = async (): Promise<void> => {
    await persist();
    goTo(STEP.milestones);
  };

  const countryName = project ? (countryLabel(project.country) ?? "") : "";
  const failure = replace.error ?? setStep.error ?? null;

  return (
    <>
      <StepPanel
        title="Compliance"
        description={
          <>
            Based on{" "}
            <strong className="font-semibold text-ink">{countryName}</strong>,
            here’s what every crew member needs on file. Documents are set by
            the country; info fields are configurable below.
          </>
        }
      >
        <div className="flex flex-col gap-7">
          <RoleCards
            jurisdiction={jurisdiction.data ?? null}
            loading={jurisdiction.isPending}
            countryName={countryName}
          />

          <section>
            <SectionLabel className={cn(BRAND_LABEL, "mb-1")}>
              Info fields collected per person
            </SectionLabel>
            <p className="mb-3 text-[0.82rem] text-muted">
              Anything else you collect from every crew member.
            </p>

            {/* `GET`/`PUT /compliance/requirements` carry no project id: this
                list is tenant-wide. During initial setup that is invisible,
                because the first project is the only project. Reopening this
                step from project B and adding a field silently changes project
                A — so in edit mode the screen says so. Scoping requirements to
                a project is the real fix and a materially larger change; this
                line is the minimum that makes the behaviour honest. */}
            {mode === "edit" && (
              <p className="mb-3 rounded-input border-[1.5px] border-brand-border bg-brand-tint-2 px-4 py-3 text-[0.84rem] font-semibold text-ink">
                These apply to every project in your company.
              </p>
            )}

            {isPending ? (
              <div
                className="flex items-center gap-2.5 py-6 text-muted"
                role="status"
              >
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[0.88rem] font-semibold">
                  Loading fields…
                </span>
              </div>
            ) : fields.length === 0 ? (
              <p className="rounded-card border-[1.5px] border-border bg-surface px-4 py-6 text-center text-[0.88rem] text-muted">
                No info fields yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {fields.map((field, index) => (
                  <li
                    key={field.id ?? `draft-${index}`}
                    className="rounded-card border-[1.5px] border-border bg-surface px-5 py-4 shadow-flat"
                  >
                    <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto]">
                      <Field label="Label" error={errors[index]}>
                        {(props) => (
                          <Input
                            {...props}
                            value={field.label}
                            maxLength={120}
                            placeholder="T-shirt size"
                            onChange={(e) =>
                              setFields((current) =>
                                current.map((f, i) =>
                                  i === index
                                    ? { ...f, label: e.target.value }
                                    : f,
                                ),
                              )
                            }
                          />
                        )}
                      </Field>
                      <Field label="Type">
                        {({ id, ...props }) => (
                          <Select
                            value={field.dataType}
                            onValueChange={(value) =>
                              setFields((current) =>
                                current.map((f, i) =>
                                  i === index
                                    ? {
                                        ...f,
                                        dataType: value as ComplianceFieldType,
                                      }
                                    : f,
                                ),
                              )
                            }
                          >
                            <SelectTrigger id={id} {...props}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <div className="flex items-end pb-1">
                        <button
                          type="button"
                          onClick={() =>
                            setFields((current) =>
                              current.filter((_, i) => i !== index),
                            )
                          }
                          aria-label={`Remove ${field.label || "field"}`}
                          className="flex size-9 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-danger"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {field.dataType === "select" && (
                      <Field
                        label="Options"
                        className="mt-4"
                        hint="Separate them with commas."
                      >
                        {(props) => (
                          <Input
                            {...props}
                            value={field.options}
                            placeholder="XS, S, M, L, XL"
                            onChange={(e) =>
                              setFields((current) =>
                                current.map((f, i) =>
                                  i === index
                                    ? { ...f, options: e.target.value }
                                    : f,
                                ),
                              )
                            }
                          />
                        )}
                      </Field>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <Button
              variant="secondary"
              className="mt-3"
              onClick={() =>
                setFields((current) => [
                  ...current,
                  { label: "", dataType: "text", options: "" },
                ])
              }
            >
              <Plus size={16} />
              Add field
            </Button>
          </section>
        </div>
      </StepPanel>

      <WizardFooter
        onBack={() => void back()}
        onContinue={() => void submit()}
        busy={replace.isPending || setStep.isPending}
        error={failure instanceof Error ? failure.message : null}
      />
    </>
  );
}

/**
 * Read-only role cards, derived from the country's jurisdiction.
 *
 * A country with no configured profile renders a note and still allows
 * Continue — the country is chosen in step 1 and must never become a dead end.
 */
export function RoleCards({
  jurisdiction,
  loading,
  countryName,
}: {
  jurisdiction: Jurisdiction | null;
  loading: boolean;
  countryName: string;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2.5 py-6 text-muted" role="status">
        <Loader2 size={18} className="animate-spin" />
        <span className="text-[0.88rem] font-semibold">
          Loading requirements…
        </span>
      </div>
    );
  }

  if (!jurisdiction) {
    return (
      <p className="rounded-card border-[1.5px] border-border bg-surface px-4 py-5 text-[0.88rem] text-muted">
        No profile configured for {countryName} — you can still continue, and
        the crew screen will simply show no document requirements.
      </p>
    );
  }

  const roles = Object.entries(jurisdiction.roleProfiles);

  return (
    <section>
      <SectionLabel className={cn(BRAND_LABEL, "mb-3")}>
        Required documents by role
      </SectionLabel>
      <ul className="grid gap-3 sm:grid-cols-2">
        {roles.map(([role, docs]) => (
          <li
            key={role}
            className="rounded-card border-[1.5px] border-border bg-surface px-5 py-4 shadow-flat"
          >
            <div className="mb-2 font-display text-[0.8rem] font-semibold uppercase tracking-[0.06em] text-ink">
              {crewRoleLabel(role)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {docs.map((doc) => (
                <span
                  key={doc}
                  className="rounded-full border-[1.5px] border-border bg-subtle px-2.5 py-[3px] text-[0.75rem] font-semibold text-ink-slate"
                >
                  {jurisdiction.docLabels[doc] ?? doc}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
